// app/lib/chat-visitor.ts — de toestand van de chatwidget bij de bezoeker.
//
// EEN STORE, GEEN REACT QUERY. QueryClientProvider wordt aangemaakt in
// routes/admin/layout.tsx:52 — op de publieke kant bestaat React Query
// helemaal niet. En zelfs als hij er wel was: berichten komen binnen via een
// stroom, niet via ophalen, dus een cachelaag zou nooit als cache worden
// gebruikt. Hetzelfde useSyncExternalStore-patroon als app/lib/toast.ts.
//
// DRIE LAGEN OM EEN GESPREK TE LATEN OVERLEVEN:
//   1. de padloze layout-route houdt de widget gemonteerd bij ZACHTE navigatie;
//   2. sessionStorage overleeft een HARDE navigatie of een herlaad;
//   3. hervatten bij mount haalt op wat er tijdens de onderbreking is gemist.
// Alle drie zijn nodig: SiteHeader navigeert nog met rauwe <a href>, dus laag 1
// alleen is niet genoeg.

import { useSyncExternalStore } from 'react';

import type { ChatAvailability, ChatMessage, ChatSession, ChatVisitorEvent } from '#shared/types/chat';
import { api, ApiError } from './api';
import { createChatStream, type ChatStreamHandle, type StreamStatus } from './chat-stream';

export type VisitorPhase =
  | 'closed' // paneel dicht, niets aan de hand
  | 'probing' // bezetting aan het ophalen
  | 'available' // er is iemand; toon het startformulier
  | 'unavailable' // niemand/gesloten/uit; toon meteen het e-mailformulier
  | 'starting' // gesprek aan het aanmaken
  | 'waiting' // in de rij
  | 'chatting' // een medewerker praat mee
  | 'ended' // afgesloten
  | 'fallback' // wachttijd verlopen; e-mailformulier met de naam al ingevuld
  | 'sent'; // e-mail verstuurd

export interface VisitorState {
  phase: VisitorPhase;
  panelOpen: boolean;
  availability: ChatAvailability | null;
  session: ChatSession | null;
  messages: ChatMessage[];
  /** Ongelezen berichten terwijl het paneel dicht is. */
  unread: number;
  agentTyping: boolean;
  connection: StreamStatus;
  /** Zichtbare foutmelding in het paneel. Bewust geen toast: `Toasts` hangt
   *  alleen in AdminShell, en de publieke widget hoort zijn eigen status te
   *  tonen op de plek waar de bezoeker al kijkt. */
  error: string;
  /** Er loopt een verzoek (starten, versturen, mailen). */
  busy: boolean;
  /** Wat er tegen een schermlezer wordt gezegd. Wordt na een paar tellen weer
   *  leeggemaakt, zodat dezelfde tekst opnieuw kan worden omgeroepen. */
  announcement: string;
}

/* ─────────────────────────────  De store  ────────────────────────────── */

const EMPTY_MESSAGES: ChatMessage[] = [];

/** Moet een STABIELE identiteit hebben: React klaagt met "The result of
 *  getServerSnapshot should be cached" zodra je hier een nieuw object maakt.
 *  Zelfde reden dat toast.ts een derde argument meegeeft. */
const SSR_SNAPSHOT: VisitorState = Object.freeze({
  phase: 'closed' as const,
  panelOpen: false,
  availability: null,
  session: null,
  messages: EMPTY_MESSAGES,
  unread: 0,
  agentTyping: false,
  connection: 'idle' as const,
  error: '',
  busy: false,
  announcement: '',
});

let state: VisitorState = SSR_SNAPSHOT;
const listeners = new Set<() => void>();

function set(patch: Partial<VisitorState>): void {
  state = { ...state, ...patch };
  for (const l of listeners) l();
}

export function useChatVisitor(): VisitorState {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => state,
    () => SSR_SNAPSHOT,
  );
}

/* ───────────────────────────  Volhouden  ─────────────────────────────── */
//
// sessionStorage en niet localStorage: een gesprek hoort bij één tabblad. Met
// localStorage zouden twee tabbladen vechten om één stroom, één typindicator en
// één ongelezen-teller. sessionStorage isoleert per tabblad en wordt leeg zodra
// het tabblad dichtgaat — precies de juiste levensduur. (app/lib/favorites.ts
// gebruikt wél localStorage, want favorieten hóren gedeeld te zijn.)
//
// Berichten worden NOOIT bewaard: die worden opgehaald. Zo is er één plek die
// over volgorde gaat.

const KEY = 'kb:chat';

interface Persisted {
  v: 1;
  sessionId: string;
  name: string;
  lastSeq: number;
  panelOpen: boolean;
}

function readPersisted(): Persisted | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Persisted>;
    if (parsed.v !== 1 || typeof parsed.sessionId !== 'string') return null;
    return {
      v: 1,
      sessionId: parsed.sessionId,
      name: String(parsed.name ?? ''),
      lastSeq: Number(parsed.lastSeq ?? 0),
      panelOpen: parsed.panelOpen === true,
    };
  } catch {
    return null;
  }
}

function persist(): void {
  try {
    if (!state.session) {
      sessionStorage.removeItem(KEY);
      return;
    }
    const payload: Persisted = {
      v: 1,
      sessionId: state.session.id,
      name: state.session.customerName,
      lastSeq: lastSeq(),
      panelOpen: state.panelOpen,
    };
    sessionStorage.setItem(KEY, JSON.stringify(payload));
  } catch {
    /* privémodus of vol: de chat mag daar niet op stuklopen */
  }
}

const lastSeq = (): number => state.messages.reduce((max, m) => (m.seq > max ? m.seq : max), 0);

/* ──────────────────────────────  Stroom  ─────────────────────────────── */

let stream: ChatStreamHandle | null = null;
let announceTimer: ReturnType<typeof setTimeout> | null = null;

function announce(text: string): void {
  set({ announcement: text });
  if (announceTimer) clearTimeout(announceTimer);
  announceTimer = setTimeout(() => set({ announcement: '' }), 4000);
}

/** Voegt een bericht toe, of laat het vallen als we het al hadden.
 *  Ontdubbelen op `seq`: het eigen bericht komt twee keer binnen — één keer als
 *  antwoord op de POST en één keer via de stroom. */
function addMessage(message: ChatMessage): void {
  if (state.messages.some((m) => m.seq === message.seq)) return;
  const messages = [...state.messages, message].sort((a, b) => a.seq - b.seq);
  const incoming = message.senderType !== 'customer';
  const unread = incoming && !state.panelOpen ? state.unread + 1 : state.unread;
  set({ messages, unread, agentTyping: false });

  if (incoming) {
    // Paneel dicht → alleen melden DAT er iets is. De inhoud voorlezen terwijl
    // iemand een artikel zit te lezen is vijandig.
    announce(state.panelOpen ? `${message.senderName}: ${message.text}` : `Nieuw bericht van ${message.senderName}`);
  }
  persist();
}

function applySession(session: ChatSession): void {
  const phase: VisitorPhase =
    session.status === 'active' ? 'chatting'
    : session.status === 'waiting' ? 'waiting'
    : session.status === 'timeout' ? 'fallback'
    : 'ended';

  set({ session, phase });

  if (phase === 'ended' || phase === 'fallback') {
    closeStream();
    if (phase === 'fallback') announce('Er is nu geen medewerker beschikbaar. Je kunt een bericht achterlaten.');
    else announce('Het gesprek is afgesloten.');
  }
  persist();
}

function openStream(sessionId: string): void {
  closeStream();
  stream = createChatStream({
    url: `/api/v1/chat/sessions/${sessionId}/stream`,
    since: () => lastSeq(),
    events: ['hello', 'resume', 'message', 'session', 'typing', 'error'],
    onStatus: (connection) => set({ connection }),
    onEvent: ({ type, data }) => {
      const event = { type, ...(data as object) } as ChatVisitorEvent;
      switch (event.type) {
        case 'hello':
          // VERVANGT de toestand — nooit samenvoegen met wat er al stond.
          set({ messages: event.messages });
          applySession(event.session);
          return;
        case 'resume':
          for (const m of event.messages) addMessage(m);
          applySession(event.session);
          return;
        case 'message':
          addMessage(event.message);
          return;
        case 'session':
          applySession(event.session);
          return;
        case 'typing':
          set({ agentTyping: event.isTyping });
          return;
        case 'error':
          set({ error: event.error.message });
          return;
      }
    },
    // Het paneel mag dicht terwijl het gesprek doorloopt: dat is precies hoe de
    // ongelezen-badge werkt. Dus niet sluiten bij een verborgen tab zolang er
    // een gesprek loopt — wel na een ruime tijd, om de socket niet eeuwig vast
    // te houden.
    hiddenGraceMs: 5 * 60_000,
  });
}

function closeStream(): void {
  stream?.close();
  stream = null;
}

/* ──────────────────────────────  Acties  ─────────────────────────────── */

async function loadAvailability(): Promise<ChatAvailability | null> {
  try {
    const availability = await api.get<ChatAvailability>('/chat/availability');
    set({ availability, error: '' });
    return availability;
  } catch {
    // Geen bezetting kunnen ophalen is geen reden om de bezoeker weg te sturen:
    // het e-mailformulier werkt nog steeds.
    set({ availability: null });
    return null;
  }
}

export const chatVisitor = {
  /** Het paneel openen. Haalt meteen de bezetting op. */
  async open(): Promise<void> {
    const wasOpen = state.panelOpen;
    set({ panelOpen: true, unread: 0, error: '' });
    persist();
    if (wasOpen) return;

    // Loopt er al een gesprek, dan hoeft de bezetting niet opnieuw.
    if (state.session && (state.phase === 'waiting' || state.phase === 'chatting')) return;
    if (state.phase === 'ended' || state.phase === 'fallback' || state.phase === 'sent') return;

    set({ phase: 'probing' });
    const availability = await loadAvailability();
    set({ phase: availability?.online ? 'available' : 'unavailable' });
  },

  close(): void {
    // Sluiten beëindigt het gesprek NIET; de stroom blijft open zodat een
    // antwoord binnenkomt terwijl de bezoeker verder leest.
    set({ panelOpen: false, error: '' });
    persist();
  },

  toggle(): void {
    if (state.panelOpen) chatVisitor.close();
    else void chatVisitor.open();
  },

  /** De bezetting opnieuw ophalen; de widget doet dit elke 30 s terwijl hij
   *  openstaat en er nog geen gesprek loopt. */
  async refreshAvailability(): Promise<void> {
    if (state.phase !== 'available' && state.phase !== 'unavailable') return;
    const availability = await loadAvailability();
    set({ phase: availability?.online ? 'available' : 'unavailable' });
  },

  async start(name: string, topic: string): Promise<void> {
    if (state.busy) return;
    set({ busy: true, error: '', phase: 'starting' });
    try {
      const data = await api.post<{ session: ChatSession; messages: ChatMessage[] }>('/chat/sessions', {
        name,
        topic,
        fromPath: typeof window === 'undefined' ? null : window.location.pathname,
      });
      set({ session: data.session, messages: data.messages, unread: 0 });
      applySession(data.session);
      openStream(data.session.id);
      announce('Je staat in de wachtrij. We zoeken een medewerker.');
    } catch (e) {
      const err = e as ApiError;
      if (err.code === 'chat_offline') {
        // Tussen het ophalen van de bezetting en het klikken is er iemand
        // weggegaan. Geen foutmelding, gewoon het e-mailformulier.
        await loadAvailability();
        set({ phase: 'unavailable' });
      } else {
        set({ phase: 'available', error: err.message || 'Het gesprek kon niet worden gestart.' });
      }
    } finally {
      set({ busy: false });
      persist();
    }
  },

  /** Geeft true bij succes, zodat het invoerveld pas dán wordt leeggemaakt —
   *  een mislukte verzending mag nooit de getypte tekst opeten. */
  async send(text: string): Promise<boolean> {
    if (!state.session || state.busy) return false;
    const trimmed = text.trim();
    if (!trimmed) return false;

    set({ busy: true, error: '' });
    try {
      const data = await api.post<{ message: ChatMessage }>(
        `/chat/sessions/${state.session.id}/messages`,
        { text: trimmed },
      );
      addMessage(data.message);
      return true;
    } catch (e) {
      const err = e as ApiError;
      set({ error: err.code === 'too_long' ? err.message : 'Je bericht kwam niet aan. Probeer het nog eens.' });
      return false;
    } finally {
      set({ busy: false });
    }
  },

  /** Een afbeelding sturen. Gaat bewust NIET via api.post: dat zet
   *  Content-Type op application/json, terwijl multer een multipart-body wil
   *  met de grens die de browser zelf bepaalt. */
  async sendImage(file: File): Promise<boolean> {
    if (!state.session) return false;
    set({ busy: true, error: '' });
    try {
      const body = new FormData();
      body.append('image', file);
      const res = await fetch(`/api/v1/chat/sessions/${state.session.id}/media`, {
        method: 'POST',
        credentials: 'same-origin',
        body,
      });
      const payload = (await res.json().catch(() => null)) as
        | { data?: { message: ChatMessage }; error?: { message: string } }
        | null;
      if (!res.ok || !payload?.data) {
        set({ error: payload?.error?.message ?? 'De afbeelding kon niet worden verstuurd.' });
        return false;
      }
      addMessage(payload.data.message);
      return true;
    } catch {
      set({ error: 'De afbeelding kon niet worden verstuurd.' });
      return false;
    } finally {
      set({ busy: false });
    }
  },

  /** Vuur-en-vergeet; een mislukte typindicator is geen nieuws. */
  typing(isTyping: boolean): void {
    if (!state.session || state.phase !== 'chatting') return;
    void api.post(`/chat/sessions/${state.session.id}/typing`, { isTyping }).catch(() => {});
  },

  async endChat(): Promise<void> {
    if (!state.session) return;
    try {
      await api.post(`/chat/sessions/${state.session.id}/close`);
    } catch {
      /* de server sluit hem sowieso op de timeout */
    }
    closeStream();
    set({ phase: 'ended' });
    persist();
  },

  /** "Nieuw gesprek" na afloop. Terug naar `probing` en NIET naar `available`:
   *  tussen het vorige gesprek en nu kan de bezetting veranderd zijn. */
  async newChat(): Promise<void> {
    closeStream();
    set({ session: null, messages: EMPTY_MESSAGES, unread: 0, agentTyping: false, error: '', phase: 'probing' });
    try {
      sessionStorage.removeItem(KEY);
    } catch {
      /* niets aan te doen */
    }
    const availability = await loadAvailability();
    set({ phase: availability?.online ? 'available' : 'unavailable' });
  },

  /** Het e-mailformulier. `website` is de honeypot: die staat verborgen in het
   *  formulier en hoort altijd leeg te blijven. */
  async sendFallback(form: { name: string; email: string; message: string; website: string }): Promise<boolean> {
    if (state.busy) return false;
    set({ busy: true, error: '' });
    try {
      await api.post('/chat/fallback', {
        ...form,
        formToken: state.availability?.formToken ?? '',
        fromPath: typeof window === 'undefined' ? null : window.location.pathname,
      });
      set({ phase: 'sent' });
      announce('Je bericht is verstuurd.');
      return true;
    } catch (e) {
      const err = e as ApiError;
      set({ error: err.message || 'Versturen mislukte. Mail ons via info@leza.nl.' });
      return false;
    } finally {
      set({ busy: false });
    }
  },

  /** Naar het e-mailformulier vanuit een lopende situatie. */
  toFallback(): void {
    set({ phase: 'fallback', error: '' });
  },

  /**
   * Hervatten bij het monteren van de widget.
   *
   * Het paneel blijft DICHT: de bezoeker heeft er niet om gevraagd. Wel wordt de
   * stroom geopend, zodat een antwoord dat binnenkomt terwijl hij verder leest
   * de ongelezen-badge laat oplichten.
   */
  async resume(): Promise<void> {
    const saved = readPersisted();
    if (!saved) return;

    try {
      const data = await api.get<{ session: ChatSession; messages: ChatMessage[] }>(
        `/chat/sessions/${saved.sessionId}`,
      );
      const alive = data.session.status === 'waiting' || data.session.status === 'active';
      set({
        session: data.session,
        messages: data.messages,
        // Alles wat na de laatst bekende positie kwam is ongelezen.
        unread: data.messages.filter((m) => m.seq > saved.lastSeq && m.senderType !== 'customer').length,
      });
      applySession(data.session);
      if (alive) openStream(saved.sessionId);
    } catch (e) {
      const err = e as ApiError;
      if (err.status === 404) {
        // Opgeruimd of verlopen: schoon opnieuw beginnen.
        try {
          sessionStorage.removeItem(KEY);
        } catch {
          /* niets aan te doen */
        }
        return;
      }
      // Netwerkfout: de toestand laten staan en het opnieuw laten proberen. NIET
      // naar het e-mailformulier — een formulier aanbieden terwijl iemand geen
      // verbinding heeft is een doodlopende weg.
      set({ error: 'Verbinding kwijt. We proberen het opnieuw.' });
    }
  },

  /** Bij het ontkoppelen van de widget. */
  teardown(): void {
    closeStream();
    if (announceTimer) clearTimeout(announceTimer);
  },
};

/* ─────────────────────  Openen vanaf elders op de pagina  ────────────── */
//
// Een venstergebeurtenis en geen React-context: SiteHeader staat binnen
// <Outlet/> en de widget is daar een SIBLING van. Er is dus geen gedeelde
// context, en die moet er ook niet komen. Zelfde constructie als
// openCommandPalette in app/components/admin/CommandPalette.tsx.

const OPEN_EVENT = 'kb:open-chat';

export function openChatWidget(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(OPEN_EVENT));
}

export function onOpenChatWidget(handler: () => void): () => void {
  window.addEventListener(OPEN_EVENT, handler);
  return () => window.removeEventListener(OPEN_EVENT, handler);
}
